import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Check, ChevronDown } from "styled-icons/feather";
import { useToaster } from "@hellocontento/maillard";
import { format } from "date-fns";
import { callApi } from "utils/ContentoApi";
import ConfirmationDialog from "components/modals/confirmationModal/ConfirmationDialog";

import ConfirmModal from "./ConfirmModal";
import GenerationLoader from "./GenerationLoader";
import * as styles from "../styles"
import CollapsibleSection from "./CollapsibleSection"
import TextRow from "./TextRow"
import CustomInstructionsRow from "./CustomInstructionsRow"
import ThemeRow, { ThemeKey } from "./ThemeRow"
import PicturesRow from "./PicturesRow"
import RenameableName from "./RenameableName"
import { formatDateForDisplay } from "../utils"
import { usePostBatchProgress } from "../usePostBatchProgress"
import { STATUS_FLOW } from "../values"
import {
  ColorTheme,
  VisualProfileSpec,
  buildGoogleFontsUrl,
  buildSelfHostedFontFaces,
  logoImageSrc,
} from "../visualProfile"

interface ProfileCardProps {
  initialProfile: {
    id: string;
    name: string;
    sourceType?: string;
    createdAt: string; // ISO timestamp; formatted for display in the card
    updatedAt: string;
    content: string;
    customInstructions?: string | null;
    status: string;
    creatingBatchId?: string | null;
  };
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDeleted: () => void;
  onRenamed?: (name: string) => void;
  accountId: string;
}

// The source-type tag shown next to the profile name. Defaults to the website
// treatment, which is by far the most common source.
const sourceLabel = (sourceType?: string): string => {
  switch (sourceType) {
    case "file":
      return "document"
    case "text":
      return "pasted text"
    default:
      return "website"
  }
}

const getStatusChip = (
  status: string,
  saveStatus: { text: string; isError: boolean },
): { text: string; tone: "success" | "error" | "pending"; showCheck: boolean } => {
  switch (status) {
    case "ready":
      return { text: saveStatus.text, tone: saveStatus.isError ? "error" : "success", showCheck: !saveStatus.isError }
    case "failed":
      return { text: "Failed", tone: "error", showCheck: false }
    default:
      return { text: "Generating…", tone: "pending", showCheck: false }
  }
}

// Pending change held while the conflict dialog is open — lets "Keep my
// changes" re-fire the same intent against the freshly bumped updatedAt.
type PendingConflict = {
  attemptedSpec: VisualProfileSpec
  serverSpec: VisualProfileSpec
  serverUpdatedAt: string
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  initialProfile,
  isExpanded,
  onToggleExpand,
  onDeleted,
  onRenamed,
  accountId,
}) => {
  const addToast = useToaster()
  const timezone = useSelector((state: { account: { data: { timezone?: string } } }) => state.account.data.timezone) ?? "Europe/Brussels"
  const [profile, setProfile] = useState(initialProfile)
  const [visualProfileSpec, setVisualProfileSpec] = useState<VisualProfileSpec | null>(null)
  const [visualProfileUpdatedAt, setVisualProfileUpdatedAt] = useState<string | null>(null)
  const [pendingConflict, setPendingConflict] = useState<PendingConflict | null>(null)
  // Only one theme row's colour/gradient picker should be open at a time.
  const [activeThemeKey, setActiveThemeKey] = useState<ThemeKey | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<{ text: string; isError: boolean }>({ text: "Saved", isError: false })
  // One optimistic lock for the whole card: every profile save (content, name, instructions)
  // bumps updatedAt server-side, so each must refresh the value the content save sends.
  const [knownUpdatedAt, setKnownUpdatedAt] = useState(initialProfile.updatedAt)
  const profileCreationDoneRef = useRef(false)

  const hasLightTheme = !!visualProfileSpec?.light_theme
  const hasDarkTheme = !!visualProfileSpec?.dark_theme
  const themeCount = (hasLightTheme ? 1 : 0) + (hasDarkTheme ? 1 : 0)
  const themesLabel = themeCount === 0 ? null : themeCount === 2 ? "2 themes" : hasLightTheme ? "light theme" : "dark theme"
  const isReady = profile.status === "ready"
  const isFailed = profile.status === "failed"
  const label = sourceLabel(profile.sourceType)
  const displayName = isReady ? profile.name : (profile.name && profile.name !== "Unknown" ? profile.name : "New profile")
  const statusChip = getStatusChip(profile.status, saveStatus)
  const logoSrc = logoImageSrc(visualProfileSpec?.light_theme?.logo)
  const profileInitial = displayName.trim().charAt(0)
  // Prefer the brand name captured during extraction; fall back to the profile/source name.
  const brandName = visualProfileSpec?.brand_name || displayName

  const isGeneratingProfile = !isReady && !!profile.creatingBatchId
  const canRename = isReady || isFailed
  const progress = usePostBatchProgress(accountId, profile.creatingBatchId, isGeneratingProfile)

  useEffect(() => {
    if (!isReady) return
    callApi(
      {
        method: "get",
        url: `content-factory/accounts/${accountId}/profiles/${profile.id}/visual-profile`,
      },
      true,
      true,
    )
      .then((visualProfile: { spec: VisualProfileSpec; updatedAt: string }) => {
        setVisualProfileSpec(visualProfile.spec)
        setVisualProfileUpdatedAt(visualProfile.updatedAt)
      })
      .catch((err: { status?: number }) => {
        if (err?.status !== 404) {
          console.error("Failed to fetch visual profile", { accountId, profileId: profile.id, err })
        }
      })
  }, [accountId, profile.id, isReady])

  // Inject font-face declarations + Google Fonts link for the loaded spec.
  // Both themes on a profile share the same fonts, so this mounts once per card.
  useEffect(() => {
    if (!visualProfileSpec) return
    const googleFontsUrl = buildGoogleFontsUrl(visualProfileSpec)
    const selfHostedFontFaces = buildSelfHostedFontFaces(visualProfileSpec)
    const injectedHeadElements: HTMLElement[] = []
    if (googleFontsUrl) {
      const linkElement = document.createElement("link")
      linkElement.rel = "stylesheet"
      linkElement.href = googleFontsUrl
      document.head.appendChild(linkElement)
      injectedHeadElements.push(linkElement)
    }
    if (selfHostedFontFaces) {
      const styleElement = document.createElement("style")
      styleElement.textContent = selfHostedFontFaces
      document.head.appendChild(styleElement)
      injectedHeadElements.push(styleElement)
    }
    return () => {
      injectedHeadElements.forEach(element => document.head.removeChild(element))
    }
  }, [visualProfileSpec])

  // PUT the visual profile. The 200 + 409 responses both carry the canonical
  // (spec, updatedAt), so no follow-up GET is ever needed to reconcile.
  const putVisualProfileSpec = async (nextSpec: VisualProfileSpec, force: boolean) => {
    try {
      const response = await callApi({
        method: "put",
        url: `content-factory/accounts/${accountId}/profiles/${profile.id}/visual-profile`,
        data: {
          spec: nextSpec,
          ...(force ? {} : { expectedUpdatedAt: visualProfileUpdatedAt }),
        },
      }, true, true)
      setVisualProfileSpec(response.spec)
      setVisualProfileUpdatedAt(response.updatedAt)
    } catch (err: any) {
      if (err?.status === 409) {
        console.error("Visual profile conflict detected", err)
        setPendingConflict({
          attemptedSpec: nextSpec,
          serverSpec: err.data.spec,
          serverUpdatedAt: err.data.updatedAt,
        })
      } else {
        console.error("Failed to save visual profile", { accountId, profileId: profile.id, err })
        addToast("Failed to save brand colors", "error")
      }
    }
  }

  // Single emit-point from ThemeRow. `nextTheme === null` means "delete this
  // theme from the spec"; otherwise it's a replacement. We bail early if the
  // replacement is reference-equal to the committed theme — that happens
  // when the user opened a picker and closed it without editing anything,
  // and we don't want a no-op PUT.
  const handleThemeChange = (themeKey: ThemeKey, nextTheme: ColorTheme | null) => {
    if (!visualProfileSpec) return
    if (nextTheme !== null && nextTheme === visualProfileSpec[themeKey]) return
    const nextSpec: VisualProfileSpec = { ...visualProfileSpec }
    if (nextTheme === null) delete nextSpec[themeKey]
    else nextSpec[themeKey] = nextTheme
    putVisualProfileSpec(nextSpec, false)
  }

  const handleOverwrite = () => {
    if (!pendingConflict) return
    putVisualProfileSpec(pendingConflict.attemptedSpec, true)
    setPendingConflict(null)
  }

  const handleLoadTheirChanges = () => {
    if (!pendingConflict) return
    setVisualProfileSpec(pendingConflict.serverSpec)
    setVisualProfileUpdatedAt(pendingConflict.serverUpdatedAt)
    setPendingConflict(null)
  }

  const handleRename = async (nextName: string) => {
    const previousName = profile.name
    if (nextName) setProfile((prev) => ({ ...prev, name: nextName }))
    try {
      const response = await callApi(
        { method: "put", url: `content-factory/accounts/${accountId}/profiles/${profile.id}/name`, data: { name: nextName } },
        true,
        true,
      )
      setProfile((prev) => ({ ...prev, name: response.name }))
      setKnownUpdatedAt(response.updatedAt)
      setSaveStatus({ text: `Saved at ${format(new Date(), "HH:mm")}`, isError: false })
      onRenamed?.(response.name)
    } catch (err) {
      console.error("Failed to rename profile", err)
      setProfile((prev) => ({ ...prev, name: previousName }))
      addToast("Failed to rename profile", "error")
    }
  }

  const handleDelete = async () => {
    try {
      await callApi(
        { method: "delete", url: `content-factory/accounts/${accountId}/profiles/${profile.id}` },
        true,
        true,
      )
      onDeleted()
    } catch (err) {
      console.error("Failed to delete profile", { accountId, profileId: profile.id, err })
      const error = err as { status?: number; data?: { message?: string; error?: string } }
      const status = error?.status
      const msg = error?.data?.message || error?.data?.error
      setDeleteErrorMessage(status && status < 500 && msg ? msg : "Failed to delete profile.")
    }
  }

  const refreshProfile = useCallback(async () => {
    try {
      const { profile: freshProfile } = await callApi(
        { method: "get", url: `content-factory/accounts/${accountId}/profiles/${initialProfile.id}` },
        true,
        true,
      )
      setProfile(freshProfile)
    } catch (err) {
      console.error("Failed to refresh profile after generation", { accountId, profileId: initialProfile.id, err })
    }
  }, [accountId, initialProfile.id])

  useEffect(() => {
    if (profileCreationDoneRef.current || !progress) return
    if (STATUS_FLOW.indexOf(progress.status) < STATUS_FLOW.indexOf("create_profile_done")) return
    profileCreationDoneRef.current = true
    refreshProfile()
  }, [progress, refreshProfile])

  const handleToggleExpand = () => {
    // Collapsing shortens the page and the browser yanks the scroll position to
    // fit; return to the top of the result page so the user lands somewhere sane.
    if (isExpanded) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
    onToggleExpand()
  }

  return (
    <styles.CardContainer>
      <styles.CardHeader onClick={handleToggleExpand} data-expanded={isExpanded}>
        <styles.SrcTile $plain={!!logoSrc}>
          {logoSrc ? (
            <styles.SrcTileLogo src={logoSrc} alt="" data-testid="profile-logo" />
          ) : (
            <styles.SrcTileLetter data-testid="profile-initial">{profileInitial}</styles.SrcTileLetter>
          )}
        </styles.SrcTile>

        <styles.CardHeaderTitle>
          <styles.ProfileName>
            {canRename ? (
              <RenameableName
                name={displayName}
                label="profile"
                testId={`rename-profile-${profile.id}`}
                onRename={handleRename}
                suffix={<styles.SourceTag>{label}</styles.SourceTag>}
              >
                {displayName}
              </RenameableName>
            ) : (
              <>
                {displayName}
                <styles.SourceTag>{label}</styles.SourceTag>
              </>
            )}
          </styles.ProfileName>
          <styles.ProfileSubtitle>Profile created {formatDateForDisplay(profile.createdAt, timezone)}</styles.ProfileSubtitle>
        </styles.CardHeaderTitle>

        <styles.CardHeaderRight>
          {isReady && themesLabel && <styles.ThemeChip>{themesLabel}</styles.ThemeChip>}
          <styles.StatusChip $tone={statusChip.tone}>
            {statusChip.showCheck && <Check size={13} />}
            {statusChip.text}
          </styles.StatusChip>
          {(isReady || isFailed) && (
            <styles.HeaderIconButton
              type="button"
              $danger
              aria-label="Delete profile"
              data-testid={`delete-profile-${profile.id}`}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                setShowDeleteModal(true)
              }}
            >
              <i className="icon-delete" style={{ fontSize: 18 }} />
            </styles.HeaderIconButton>
          )}
          <styles.HeaderIconButton type="button" aria-label={isExpanded ? "Collapse" : "Expand"}>
            <styles.ExpandIcon isExpanded={isExpanded}>
              <ChevronDown size={18} />
            </styles.ExpandIcon>
          </styles.HeaderIconButton>
        </styles.CardHeaderRight>
      </styles.CardHeader>

      <styles.CardBody isOpen={isExpanded}>
        <styles.CardContent>
          <styles.CardInner>
            {!isReady && profile.creatingBatchId && (
              <GenerationLoader
                status={progress?.status}
                health={progress?.health}
                updates={progress?.updates}
                estimatedCompletionAt={progress?.estimatedCompletionAt}
                sourceType={progress?.sourceType ?? profile.sourceType}
                showProfileStep
                onDismiss={handleDelete}
              />
            )}
            {isReady && (
              <>
                <CollapsibleSection
                  title="Business knowledge"
                  description="What Willow learned about the company. Every post is written from it."
                  testId={`profile-section-knowledge-${profile.id}`}
                >
                  <TextRow
                    accountId={accountId}
                    profile={profile}
                    knownUpdatedAt={knownUpdatedAt}
                    onUpdatedAtChange={setKnownUpdatedAt}
                    onSaveStatusChange={setSaveStatus}
                  />
                </CollapsibleSection>
                <styles.RowDivider />
                <CollapsibleSection
                  title="Custom instructions"
                  description="Your own rules for the post text, for example a maximum length."
                  testId={`profile-section-instructions-${profile.id}`}
                >
                  <CustomInstructionsRow
                    accountId={accountId}
                    profileId={profile.id}
                    customInstructions={profile.customInstructions ?? null}
                    onUpdatedAtChange={setKnownUpdatedAt}
                    onSaveStatusChange={setSaveStatus}
                  />
                </CollapsibleSection>
                {visualProfileSpec && (hasLightTheme || hasDarkTheme) && (
                  <>
                    <styles.RowDivider />
                    <CollapsibleSection
                      title="Brand style"
                      description="The colors, fonts and logo used in the visuals."
                      testId={`profile-section-brand-${profile.id}`}
                    >
                      {hasLightTheme && (
                        <ThemeRow
                          spec={visualProfileSpec}
                          themeKey="light_theme"
                          accountId={accountId}
                          profileId={profile.id}
                          onThemeChange={handleThemeChange}
                          logoFallbackBrandName={brandName}
                          isActive={activeThemeKey === "light_theme"}
                          onRequestActive={() => setActiveThemeKey("light_theme")}
                        />
                      )}
                      {hasDarkTheme && (
                        <ThemeRow
                          spec={visualProfileSpec}
                          themeKey="dark_theme"
                          accountId={accountId}
                          profileId={profile.id}
                          onThemeChange={handleThemeChange}
                          logoFallbackBrandName={brandName}
                          isActive={activeThemeKey === "dark_theme"}
                          onRequestActive={() => setActiveThemeKey("dark_theme")}
                        />
                      )}
                    </CollapsibleSection>
                  </>
                )}
                <styles.RowDivider />
                <CollapsibleSection
                  title="Pictures"
                  description="Your own photos, for Create to use in the visuals."
                  testId={`profile-section-pictures-${profile.id}`}
                >
                  <PicturesRow accountId={accountId} profileId={profile.id} isWebsiteProfile={profile.sourceType === "website"} />
                </CollapsibleSection>
              </>
            )}
          </styles.CardInner>
        </styles.CardContent>
      </styles.CardBody>

      <ConfirmModal
        modalName="delete-profile-modal"
        title="Delete this profile?"
        description={`You are about to delete the profile created from ${profile.name}. This can't be undone.`}
        icon={<span className="icon-delete" />}
        confirmLabel="Delete profile"
        confirmVariant="danger"
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await handleDelete()
          setShowDeleteModal(false)
        }}
      />

      <ConfirmationDialog
        isOpen={deleteErrorMessage !== null}
        title="Can't delete this profile"
        description={deleteErrorMessage}
        confirmLabel="OK"
        onConfirm={() => setDeleteErrorMessage(null)}
      />

      <ConfirmationDialog
        isOpen={pendingConflict !== null}
        title="Visual profile modified"
        description="This visual profile was modified by someone else while you were editing. What would you like to do?"
        cancelLabel="Keep My Changes"
        confirmLabel="Load Their Changes"
        onCancel={handleOverwrite}
        onConfirm={handleLoadTheirChanges}
      />
    </styles.CardContainer>
  )
}
